import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { FormattedData } from '../globalTypes'
import { formatGroupedData, getTotals, handleChartColors, handleChartHeight } from '../utils'
import { AgentTransactionReportProps, TransactionTotal } from './types'
import { getGroupData, getTransactionOveralls } from './utils'

const AgentTransactionReportChart = (props: AgentTransactionReportProps) => {
    const { t } = useTranslation()
    const overallLabel = t('app.common.overall')
    const totalLabel = t('app.common.total')

    const { reportsData } = props
    const reports = useMemo(() => reportsData?.data || [], [reportsData])
    const months = useMemo(() => reportsData?.timePeriod || [], [reportsData])

    const formattedData = formatGroupedData({
        data: reports,
        dataKey: 'agent',
        handleGroupData: (transactionTotal: TransactionTotal, agentName: string) => getGroupData(transactionTotal, agentName),
    }) as FormattedData[]

    const { monthOveralls } = useMemo(() => getTransactionOveralls(reports, months), [reports, months])

    // adds month overalls data ("Sveukupno") to formattedData array
    const dataWithOveralls = useMemo(() => {
        return formattedData.map((data) => {
            const monthOverallObj = monthOveralls.find((month) => month.id === data.id)

            return {
                ...data,
                monthName: monthOverallObj?.monthName,
                [overallLabel]: monthOverallObj?.count,
            }
        })
    }, [formattedData, monthOveralls, overallLabel])

    const totalsObj = useMemo(() => {
        const transactionTotals = getTotals(reports)
        const totalOverall = monthOveralls.reduce((acc, curr) => acc + curr.count, 0)

        // totalLabel (e.g. 'Ukupno') is mapped to monthName to be displayed on y axis together with months
        return {
            id: 'Ukupno',
            monthName: totalLabel,
            ...transactionTotals,
            [overallLabel]: totalOverall,
        }
    }, [reports, monthOveralls, totalLabel, overallLabel])

    const chartData = [totalsObj, ...dataWithOveralls]

    const numObjects = chartData.length
    const numFields = Object.keys(chartData[0]).length - 2 // exclude id, monthName
    const [totalHeight, barSize, barGap] = handleChartHeight(numObjects, numFields)

    const { overallColor, reportColors } = handleChartColors(reports.length)

    return (
        <div style={{ height: `${totalHeight}px` }} className="w-full">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    layout="vertical"
                    data={chartData}
                    barSize={barSize}
                    barGap={barGap}
                    margin={{
                        top: 20,
                        right: 30,
                        left: 28,
                        bottom: 50,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="monthName" type="category" tickMargin={10} />
                    <Tooltip formatter={(value: number) => (value > 0 ? value : 0)} contentStyle={{ fontSize: '11px' }} />
                    <Legend iconSize={16} />

                    <Bar key="overall" dataKey={overallLabel} fill={overallColor}>
                        <LabelList
                            dataKey={overallLabel}
                            position="right"
                            fill="#000"
                            fontSize={12}
                            formatter={(value: number) => (value > 0 ? value : '')}
                        />
                    </Bar>

                    {reports?.map(({ agent }, index) => {
                        if (!agent) return null

                        const agentName = `${agent.firstName} ${agent.lastName}`

                        return (
                            <Bar key={agent.id} dataKey={agentName} fill={reportColors[index]}>
                                <LabelList
                                    dataKey={agentName}
                                    position="right"
                                    fill="#000"
                                    fontSize={12}
                                    formatter={(value: number) => (value > 0 ? value : '')}
                                />
                            </Bar>
                        )
                    })}
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}

export default AgentTransactionReportChart
